import { LoadingState } from "@repo/app-types/client/client";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Checkbox } from "@repo/ui/Checkbox/checkbox";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { IconCreditCard } from "@tabler/icons-react";
import { type FC, useEffect } from "react";
import { useNavigate } from "react-router";
import { appPaths } from "../../../app/appPaths.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import {
  fetchBillingInfo,
  selectBillingLoadingState,
  selectDefaultPaymentMethod,
} from "../../billing/billingSlice.js";

const incentiveDropdownOptions = [
  { label: "None", value: 0 },
  { label: "$5 gift card", value: 5 },
  { label: "$10 gift card", value: 10 },
  { label: "$15 gift card", value: 15 },
  { label: "$20 gift card", value: 20 },
  { label: "$25 gift card", value: 25 },
  { label: "$35 gift card", value: 35 },
  { label: "$50 gift card", value: 50 },
  { label: "$60 gift card", value: 60 },
  { label: "$70 gift card", value: 70 },
  { label: "$100 gift card", value: 100 },
  { label: "$150 gift card", value: 150 },
  { label: "$200 gift card", value: 200 },
];

interface IncentiveSettingsSectionProps {
  incentiveQuantity: number | null;
  isIncentiveCharityOnly: boolean;
  onIncentiveQuantityChange: (incentiveQuantity: number | null) => void;
  onIncentiveCharityOnlyChange: (isIncentiveCharityOnly: boolean) => void;
}

export const IncentiveSettingsSection: FC<IncentiveSettingsSectionProps> = ({
  incentiveQuantity,
  isIncentiveCharityOnly,
  onIncentiveQuantityChange,
  onIncentiveCharityOnlyChange,
}) => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const defaultPaymentMethod = useAppSelector(selectDefaultPaymentMethod);
  const billingLoadingState = useAppSelector(selectBillingLoadingState);

  // Load billing info if necessary
  useEffect(() => {
    if (billingLoadingState !== LoadingState.LOADED)
      dispatch(fetchBillingInfo());
  }, []);

  return (
    <div>
      <Label size={Size.LARGE}>Incentive</Label>
      <div className="flex flex-row items-end space-x-4">
        <div className="mt-2 w-52">
          <SimpleSelect
            options={incentiveDropdownOptions}
            value={incentiveQuantity}
            placeholder="Select an incentive"
            onChange={onIncentiveQuantityChange}
          />
        </div>
        {!defaultPaymentMethod &&
          billingLoadingState === LoadingState.LOADED && (
            <div>
              <Button
                variant={ButtonVariant.Secondary}
                icon={<IconCreditCard className="h-5 w-5" />}
                onClick={() => {
                  navigate(appPaths.settingsBilling.build());
                }}
              >
                Add payment method
              </Button>
            </div>
          )}
      </div>
      <div className="mt-2 ml-1">
        <Checkbox
          isChecked={isIncentiveCharityOnly}
          onChange={(isChecked) => onIncentiveCharityOnlyChange(isChecked)}
          label="Only provide charity donation options to the participant"
        />
      </div>
      <div className="mt-2 text-sm text-muted-foreground">
        Incentives drive higher quality feedback and reduce selection bias.
        Participants receive a gift card (choice of Amazon, Visa, charity
        donation, and more) after they complete an interview. 100% of your
        incentive goes to the participant.
      </div>
    </div>
  );
};
